import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { setBadge, subscribeToPush } from "../lib/push";
import { Bell, X, CheckCircle, Warning } from "@phosphor-icons/react";

const POLL_MS = 30000;
const SEEN_KEY = "athlon_last_notif_ts";

function showBrowserNotification(title, body) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(title, {
          body: body || "",
          icon: "/logo192.png",
          badge: "/favicon-32.png",
          vibrate: [200, 100, 200],
          tag: "athlon-" + Date.now(),
        });
      }).catch(() => {});
    } else {
      new Notification(title, { body: body || "", icon: "/logo192.png" });
    }
  } catch {}
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      // Compare with last seen ts
      const lastSeen = Number(localStorage.getItem(SEEN_KEY) || 0);
      const fresh = data.filter((n) => new Date(n.created_at).getTime() > lastSeen && !n.read);
      if (fresh.length > 0) {
        fresh.slice(0, 3).forEach((n) => showBrowserNotification(n.title, n.body));
        localStorage.setItem(SEEN_KEY, String(Date.now()));
      }
      setItems(data);
      // Update PWA app icon badge
      const unreadCount = data.filter((n) => !n.read).length;
      setBadge(unreadCount);
    } catch {
      /* silent - not logged in */
    }
  }, []);

  useEffect(() => {
    // init seen key
    if (!localStorage.getItem(SEEN_KEY)) {
      localStorage.setItem(SEEN_KEY, String(Date.now()));
    }
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const unread = items.filter((n) => !n.read).length;

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      return;
    }
    const res = await subscribeToPush();
    setPermission(Notification.permission);
    if (res.ok) {
      try {
        const { toast } = await import("sonner");
        toast.success("Push notifications activadas");
      } catch {}
    }
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) await api.put(`/notifications/${n.notification_id}/read`);
      load();
      if (n.link) {
        setOpen(false);
        navigate(n.link);
      }
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      load();
    } catch {}
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      load();
    } catch {}
  };

  const iconFor = (type) => {
    if (type === "critical" || type === "warning") return <Warning size={18} weight="duotone" className="text-[#FF3B30]" />;
    if (type === "success") return <CheckCircle size={18} weight="duotone" className="text-[#32D74B]" />;
    return <Bell size={18} weight="duotone" className="text-accent-blue" />;
  };

  const fmtRel = (iso) => {
    const d = new Date(iso).getTime();
    const s = Math.floor((Date.now() - d) / 1000);
    if (s < 60) return "ahora";
    if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
    if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
    return `hace ${Math.floor(s / 86400)} d`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-muted hover:text-white transition-colors"
        data-testid="notifications-bell"
        aria-label="Notificaciones"
      >
        <Bell size={22} weight="bold" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notif-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[70vh] bg-surface border border-app shadow-2xl z-40 flex flex-col" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-app">
            <div className="font-display font-bold uppercase text-sm tracking-widest">Notificaciones</div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[10px] uppercase tracking-widest text-muted hover:text-white" data-testid="mark-all-read">
                Marcar todas leídas
              </button>
            )}
          </div>

          {permission !== "granted" && "Notification" in window && (
            <button onClick={requestPermission} className="text-xs text-accent-blue hover:text-white px-4 py-2 border-b border-app text-left" data-testid="enable-push">
              🔔 Activar notificaciones push del navegador
            </button>
          )}

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-8 text-center text-muted text-sm">No tenés notificaciones</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.notification_id}
                  className={`p-3 border-b border-app flex gap-3 cursor-pointer hover:bg-surface-hover transition-colors ${!n.read ? "bg-[#0A0A0A]" : ""}`}
                  onClick={() => handleClick(n)}
                  data-testid={`notif-${n.notification_id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">{iconFor(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${!n.read ? "text-white font-semibold" : "text-muted"}`}>
                      {n.title}
                    </div>
                    {n.body && <div className="text-xs text-muted mt-1 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-muted mt-1">{fmtRel(n.created_at)}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(n.notification_id); }} className="text-muted hover:text-[#FF3B30] flex-shrink-0" data-testid={`delete-notif-${n.notification_id}`}>
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
